import React, { useCallback, useEffect, useState } from "react";
import { api } from "./api";
import RulesRescoreResult from "./RulesRescoreResult";

const LIVE = ["queued", "running"];

// Dashboard card for the latest re-score job. Jobs are durable on the server (batches + cursor), so this
// reloads the current state on every visit, polls while the job is live, and offers Pause / Resume.
export default function RescoreJobPanel({ styles, startedJobId, onFinished }) {
  const [job, setJob] = useState(null);
  const [msg, setMsg] = useState("");
  const load = useCallback(async (id) => {
    try {
      if (id) { const r = await api.rescoreStatus(id); setJob(r.data); return r.data; }
      const r = await api.rescoreJobs();
      const j = (r.data.jobs || [])[0] || null;
      setJob(j);
      return j;
    } catch (_) { return null; }
  }, []);
  useEffect(() => { load(startedJobId); }, [load, startedJobId]);
  useEffect(() => {
    if (!job || !LIVE.includes(job.status)) return undefined;
    const t = setInterval(async () => {
      const j = await load(job.id);
      if (j && !LIVE.includes(j.status) && onFinished) onFinished(j);
    }, 1500);
    return () => clearInterval(t);
  }, [job, load, onFinished]);
  if (!job) return null;
  const pct = job.total ? Math.min(100, Math.round((100 * (job.processed || 0)) / job.total)) : (job.status === "done" ? 100 : 0);
  const act = async (fn, label) => {
    try { await fn(job.id); setMsg(label); await load(job.id); } catch (ex) { setMsg("Error: " + ex.message); }
  };
  const resumable = ["paused", "failed"].includes(job.status) || (job.status === "running" && job.stale);
  return (
    <div style={styles.card} data-testid="rescore-job">
      <div style={styles.row}>
        <strong>{job.kind === "rules_only" ? "Rules-only re-score" : `Re-score${job.force ? " (forced model)" : ""}`}</strong>
        <span data-testid="rescore-job-status">{job.status}</span>
        <span style={{ color: "var(--hs-text-muted)", fontSize: 12 }} data-testid="rescore-job-progress">
          {job.processed ?? 0} / {job.total ?? 0} items ({pct}%){job.errors > 0 && ` · ${job.errors} error(s)`}
          {job.resumed > 0 && ` · resumed ${job.resumed}×`}
        </span>
        <span style={{ marginLeft: "auto" }}>
          {LIVE.includes(job.status) && (
            <button data-testid="rescore-job-pause" style={styles.btnDim} onClick={() => act(api.rescorePause, "Paused.")}>Pause</button>)}
          {resumable && (
            <button data-testid="rescore-job-resume" style={styles.btn} onClick={() => act(api.rescoreResume, "Resuming where it stopped…")}>Resume</button>)}
        </span>
      </div>
      <div style={{ background: "var(--hs-border)", height: 6, borderRadius: 3, overflow: "hidden", margin: "6px 0" }}>
        <div data-testid="rescore-job-bar" style={{ width: `${pct}%`, height: "100%", background: job.status === "failed" ? "var(--hs-danger)" : "var(--hs-accent)" }} />
      </div>
      {job.last_error && <div style={{ color: "var(--hs-danger)", fontSize: 12 }} data-testid="rescore-job-error">Stopped: {job.last_error} — Resume continues from item {job.processed}.</div>}
      {msg && <div style={{ color: "var(--hs-text-muted)", fontSize: 12 }}>{msg}</div>}
      {job.kind === "rules_only"
        ? <RulesRescoreResult job={job} styles={{ ...styles, card: {} }} />
        : (
          <div style={{ fontSize: 12, color: "var(--hs-text-muted)" }} data-testid="rescore-job-llm">
            skipped {job.skipped ?? 0}{job.missing_content > 0 && ` · ${job.missing_content} content missing`}
            {" "}· model: {job.llm?.ok ?? 0} ok · {job.llm?.error ?? 0} error · {job.llm?.cap ?? 0} stopped by cap
            {" "}· why: {Object.entries(job.llm?.reasons || {}).map(([k, v]) => `${k} ${v}`).join(", ") || "—"}
          </div>)}
    </div>
  );
}
